import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Table, Spinner, Alert, Badge } from 'react-bootstrap';
import api from '../services/api';
import { showToast } from './ToastNotification';
import { formatPrice, formatDate } from './OrderCells';
import { typDokladu } from './invoiceActions';

/**
 * Vytvoření faktury přímo z objednávky — stejná logika jako v detailu projektu
 * (POST /invoices/create z booking_item ID), jen pro položky jedné objednávky.
 * Položky se čtou čerstvě z API, tedy v uloženém stavu objednávky.
 *
 * Předvybrané jsou položky, které nejsou stornované a ještě nejsou na žádné
 * faktuře. Už fakturované jdou zaškrtnout ručně (např. daňový doklad po záloze).
 */
function OrderInvoiceModal({ show, onHide, orderId, orderNumber, openTab }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [currency, setCurrency] = useState('CZK');
  const [selected, setSelected] = useState(new Set());
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [invoiceType, setInvoiceType] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  // Kurz pro doklad v cizí měně: `cnb` = kurz ČNB k DUZP (doporučený), `order` =
  // kurz z objednávky (ten je z data jejího založení), `custom` = vlastní.
  const [orderRate, setOrderRate] = useState(null);
  const [rateMode, setRateMode] = useState('cnb');
  const [cnbRate, setCnbRate] = useState(null);
  const [cnbLoading, setCnbLoading] = useState(false);
  const [cnbError, setCnbError] = useState('');
  const [customRate, setCustomRate] = useState('');

  useEffect(() => {
    if (!show || !orderId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [orderRes, enumsRes] = await Promise.all([api.getOrder(orderId), api.getInvoiceEnums()]);
        if (cancelled) return;
        const orderItems = [...(orderRes.data?.items || [])]
          .sort((a, b) => String(a.number || '').localeCompare(String(b.number || ''), 'cs', { numeric: true }));
        setItems(orderItems);
        setCurrency(orderRes.data?.currency || 'CZK');
        setOrderRate(orderRes.data?.exchange_rate ? parseFloat(orderRes.data.exchange_rate) : null);
        setSelected(new Set(
          orderItems
            .filter(i => i.state !== 'Canceled' && (i.invoices || []).length === 0)
            .map(i => i.id)
        ));
        const types = enumsRes.data?.types || [];
        setInvoiceTypes(types);
        setInvoiceType(types[0]?.id || '');
      } catch (err) {
        if (!cancelled) setError(err.message || 'Nepodařilo se načíst položky objednávky.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [show, orderId]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // DUZP dokladu = nejpozdější datum vydání vybraných položek, jinak včerejšek —
  // stejné pravidlo jako v InvoiceService::createSingleInvoice(). Podle něj se bere
  // kurz, protože pro DPH se přepočítává ke dni uskutečnění plnění.
  const duzp = useMemo(() => {
    const dny = items
      .filter(i => selected.has(i.id))
      .map(i => i.project_date || i.date)
      .filter(Boolean)
      .map(d => String(d).slice(0, 10))
      .sort();
    if (dny.length > 0) return dny[dny.length - 1];
    const vcera = new Date();
    vcera.setDate(vcera.getDate() - 1);
    return vcera.toISOString().slice(0, 10);
  }, [items, selected]);

  useEffect(() => {
    if (!show || currency === 'CZK' || !duzp) {
      setCnbRate(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setCnbLoading(true);
      setCnbError('');
      try {
        const res = await api.getExchangeRateForDate(duzp, currency);
        if (!cancelled) setCnbRate(res.data || null);
      } catch (err) {
        if (!cancelled) {
          setCnbRate(null);
          setCnbError(err.message || 'Kurz ČNB se nepodařilo zjistit.');
        }
      } finally {
        if (!cancelled) setCnbLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [show, currency, duzp]);

  // Bez kurzu ČNB (nedostupná, nebo měna, kterou nekótuje) se nabídka přepne
  // na kurz z objednávky, ať formulář nedrží prázdnou volbu.
  useEffect(() => {
    if (rateMode === 'cnb' && !cnbLoading && !cnbRate && orderRate > 0) {
      setRateMode('order');
    }
  }, [rateMode, cnbLoading, cnbRate, orderRate]);

  const kurz = (() => {
    if (currency === 'CZK') return null;
    if (rateMode === 'cnb') return cnbRate ? parseFloat(cnbRate.rate_per_unit) : null;
    if (rateMode === 'order') return orderRate > 0 ? orderRate : null;
    const vlastni = parseFloat(String(customRate).replace(',', '.'));
    return vlastni > 0 ? vlastni : null;
  })();
  const kurzChybi = currency !== 'CZK' && !(kurz > 0);

  const itemsCanceled = items.filter(i => i.state === 'Canceled');
  const itemsInvoiced = items.filter(i => i.state !== 'Canceled' && (i.invoices || []).length > 0);
  const itemsToInvoice = items.filter(i => i.state !== 'Canceled' && (i.invoices || []).length === 0);

  const selectableIds = items.filter(i => i.state !== 'Canceled').map(i => i.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));

  const selectedTotal = items
    .filter(i => selected.has(i.id))
    .reduce((sum, i) => sum + (parseFloat(i.net_net_price) || 0), 0);
  const selectedInvoiced = items.filter(i => selected.has(i.id) && (i.invoices || []).length > 0);

  const handleCreate = async () => {
    if (creating || selected.size === 0 || !invoiceType || kurzChybi) return;
    setCreating(true);
    try {
      const result = await api.createInvoices({
        bookingItemIds: [...selected],
        invoiceType,
        separateCompanies: [],
        // Jen u cizí měny; korunový doklad kurz nemá
        ...(kurz > 0 ? { exchangeRate: kurz } : {}),
      });
      if (result.status === 'ok' && result.data) {
        const warnings = result.data.aresWarnings || [];
        let msg = 'Faktura byla vytvořena.';
        if (warnings.length > 0) {
          msg += '\n⚠ ' + warnings.join('\n⚠ ');
        }
        showToast(msg, warnings.length > 0 ? 'warning' : 'success');
        // Otevřená záložka Fakturace se načte znovu, ať tam nová faktura je
        window.dispatchEvent(new CustomEvent('invoices:refresh'));
        window.dispatchEvent(new CustomEvent('orders:refresh'));
        onHide();
        const invoiceId = result.data.invoices?.[0];
        if (invoiceId && openTab) {
          openTab(`invoice-${invoiceId}`, 'Faktura', `/invoices/${invoiceId}`, 'InvoiceDetail', { invoiceId });
        }
      } else {
        showToast(result.message || 'Chyba při vytváření faktury.', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Nepodařilo se vytvořit fakturu.', 'danger');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={() => !creating && onHide()} size="lg" centered>
      <Modal.Header closeButton={!creating}>
        <Modal.Title>
          <i className="bi bi-receipt me-2"></i>Vytvořit fakturu{orderNumber ? ` – ${orderNumber}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" className="me-2" />Načítání…</div>
        ) : (
          <>
            <div className="small text-muted mb-2">
              Faktura se vytvoří z uložené objednávky. Neuložené změny ve formuláři se nepoužijí.
            </div>
            <div className="small mb-2">
              <Badge bg="secondary" className="me-2">
                <i className="bi bi-hourglass-split me-1"></i>K fakturaci {itemsToInvoice.length}
              </Badge>
              <Badge bg="success" className="me-2">
                <i className="bi bi-receipt me-1"></i>Fakturováno {itemsInvoiced.length}
              </Badge>
              {itemsCanceled.length > 0 && (
                <Badge bg="light" text="dark" className="border">Storno {itemsCanceled.length}</Badge>
              )}
            </div>
            <Table size="sm" hover className="align-middle mb-3">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 36 }}>
                    <Form.Check type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectableIds.length === 0} />
                  </th>
                  <th>Položka</th>
                  <th>Datum vydání</th>
                  <th>Projekt / formát</th>
                  <th className="text-end">Cena bez DPH</th>
                  <th>Stav fakturace</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const canceled = item.state === 'Canceled';
                  const invoices = item.invoices || [];
                  const rowClass = canceled ? 'text-muted' : invoices.length > 0 ? 'table-success' : '';
                  return (
                    <tr key={item.id} className={rowClass} role="button" onClick={() => !canceled && toggle(item.id)}>
                      <td onClick={e => e.stopPropagation()}>
                        <Form.Check type="checkbox" checked={selected.has(item.id)} disabled={canceled} onChange={() => toggle(item.id)} />
                      </td>
                      <td className="text-nowrap">
                        {item.number || item.id}
                        {canceled && <span className="badge bg-secondary ms-2">Storno</span>}
                      </td>
                      <td className="text-nowrap">{formatDate(item.project_date || item.date)}</td>
                      <td>
                        <div>{item.project_name || item.medium_name || '—'}</div>
                        {item.ratecard_item_name && <div className="small text-muted">{item.ratecard_item_name}</div>}
                      </td>
                      <td className="text-end text-nowrap">
                        {/* Ceny v objednávce jsou vždy v korunách (ceník je v korunách),
                            doklad v cizí měně je nese přepočtené — proto se tady ukazuje
                            korunová částka a pod ní přepočet vybraným kurzem. */}
                        {formatPrice(item.net_net_price)} CZK
                        {kurz > 0 && (
                          <div className="small text-muted">
                            ≈ {formatPrice((parseFloat(item.net_net_price) || 0) / kurz)} {currency}
                          </div>
                        )}
                      </td>
                      <td className="small text-nowrap">
                        {canceled
                          ? <Badge bg="light" text="dark" className="border">Storno</Badge>
                          : invoices.length > 0
                            ? (
                              <Badge bg="success" title={`Na dokladech: ${invoices.map(inv => `${typDokladu(inv.type)} ${inv.number || inv.id}`).join(', ')}`}>
                                <i className="bi bi-receipt me-1"></i>
                                {invoices.map(inv => `${typDokladu(inv.type)} ${inv.number || inv.id}`).join(' · ')}
                              </Badge>
                            )
                            : <Badge bg="secondary"><i className="bi bi-hourglass-split me-1"></i>K fakturaci</Badge>}
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr><td colSpan="6" className="text-center text-muted py-3">Objednávka nemá žádné položky</td></tr>
                )}
              </tbody>
            </Table>

            {selectedInvoiced.length > 0 && (
              <Alert variant="warning" className="py-2 small">
                <i className="bi bi-exclamation-triangle me-1"></i>
                Vybrané položky {selectedInvoiced.map(i => i.number).join(', ')} už jsou na faktuře.
              </Alert>
            )}

            {currency !== 'CZK' && (
              <div className="border rounded p-2 mb-3">
                <div className="fw-bold mb-1">
                  Kurz pro přepočet <span className="text-muted fw-normal">({currency} → CZK)</span>
                </div>
                <div className="small text-muted mb-2">
                  Doklad má datum uskutečnění plnění <strong>{formatDate(duzp)}</strong> —
                  pro DPH se přepočítává kurzem k tomuto dni, ne ke dni vystavení.
                </div>

                <Form.Check
                  type="radio"
                  id="kurz-cnb"
                  name="kurz"
                  className="mb-1"
                  checked={rateMode === 'cnb'}
                  disabled={!cnbRate}
                  onChange={() => setRateMode('cnb')}
                  label={
                    cnbLoading
                      ? <><Spinner animation="border" size="sm" className="me-1" />Zjišťuji kurz ČNB…</>
                      : cnbRate
                        ? <>
                            Kurz ČNB <strong>{formatPrice(cnbRate.rate_per_unit)} CZK</strong> za 1 {currency}
                            {cnbRate.rate_date !== cnbRate.requested_date && (
                              <span className="text-muted small">
                                {' '}— ČNB pro {formatDate(cnbRate.requested_date)} kurz nevydala,
                                platí lístek z {formatDate(cnbRate.rate_date)}
                              </span>
                            )}
                            {cnbRate.source === 'fallback' && (
                              <span className="text-warning small">
                                {' '}— ČNB je nedostupná, tohle je poslední známý kurz z {formatDate(cnbRate.rate_date)}
                              </span>
                            )}
                          </>
                        : <span className="text-muted">Kurz ČNB k {formatDate(duzp)} není k dispozici{cnbError ? ` (${cnbError})` : ''}</span>
                  }
                />

                <Form.Check
                  type="radio"
                  id="kurz-objednavka"
                  name="kurz"
                  className="mb-1"
                  checked={rateMode === 'order'}
                  disabled={!(orderRate > 0)}
                  onChange={() => setRateMode('order')}
                  label={orderRate > 0
                    ? <>Kurz z objednávky <strong>{formatPrice(orderRate)} CZK</strong> za 1 {currency}</>
                    : <span className="text-muted">Objednávka kurz nemá</span>}
                />

                <Form.Check
                  type="radio"
                  id="kurz-vlastni"
                  name="kurz"
                  checked={rateMode === 'custom'}
                  onChange={() => setRateMode('custom')}
                  label="Vlastní kurz"
                />
                {rateMode === 'custom' && (
                  <div className="d-flex align-items-center gap-2 mt-1 ms-4">
                    <Form.Control
                      size="sm"
                      style={{ maxWidth: '8rem' }}
                      value={customRate}
                      onChange={e => setCustomRate(e.target.value)}
                      placeholder="např. 24,355"
                    />
                    <span className="small text-muted">CZK za 1 {currency}</span>
                  </div>
                )}

                {kurzChybi && (
                  <Alert variant="warning" className="py-2 small mb-0 mt-2">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    Bez kurzu nejde doklad v měně {currency} vystavit — částky v objednávce
                    jsou v korunách a nebylo by čím je přepočítat.
                  </Alert>
                )}
              </div>
            )}

            <Form.Group>
              <Form.Label className="fw-bold">Typ dokladu</Form.Label>
              <Form.Select value={invoiceType} onChange={e => setInvoiceType(e.target.value)}>
                {invoiceTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </Form.Select>
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="me-auto small text-muted">
          Vybráno {selected.size} {selected.size === 1 ? 'položka' : selected.size >= 2 && selected.size <= 4 ? 'položky' : 'položek'}
          {' · '}{formatPrice(selectedTotal)} CZK bez DPH
          {kurz > 0 && <> ≈ <strong>{formatPrice(selectedTotal / kurz)} {currency}</strong></>}
        </div>
        <Button variant="outline-secondary" onClick={onHide} disabled={creating}>Zrušit</Button>
        <Button variant="primary" onClick={handleCreate} disabled={loading || creating || selected.size === 0 || !invoiceType || kurzChybi}>
          {creating
            ? <><Spinner animation="border" size="sm" className="me-1" />Vytvářím…</>
            : <><i className="bi bi-check-lg me-1"></i>Vytvořit fakturu</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default OrderInvoiceModal;
